import { set } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
// py-components
import {
	ButtonStandard,
	Checkbox,
	FormDatePicker,
	type FormHandle,
	FormInputNumeric,
	FormSingleSelect,
	FormSubscribe,
	FormTextEditor,
	Icon,
	ModalBase,
	PYForm,
	SubmitButton,
	Text,
	Tooltip,
	toast,
	usePyForm,
} from "@performyard/py-components";

// hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../../../../api/hooks";
import { useOutlooks } from "../../../../../../api/hooks/defs/outlook";
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";

// types
import type { Goal } from "../../../../../../api/services";

// utils
import { parseGoalPermissionsForUser } from "../../../utils";
import { getProgressLabels } from "../../SingleGoalInfo/utils";
import { type FormValues, formSchema } from "./utils";

// config
import Config from "../../../config";

type UpdateProgressModalProps = {
	goalId?: string;
	isOpen?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
};

const UpdateProgressModal = ({
	goalId,
	isOpen: isOpenProp,
	onClose,
	onSuccess,
}: UpdateProgressModalProps): React.ReactElement => {
	const formRef = useRef<FormHandle<typeof formSchema>>(null);
	const { goal_id: goalIdFromParams } = useParams<{ goal_id: string }>();
	const resolvedGoalId = goalId ?? goalIdFromParams ?? "";

	// Data fetching
	const { data: permissions } = useRbacObjectPermissions("goal", resolvedGoalId);
	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: resolvedGoalId });
	const goal = data ? data.goal : ({} as Goal);
	const { data: outlooksData, isLoading: isLoadingOutlooks } = useOutlooks();

	const { refetch: refetchGoalProgress } = useGoalHistory({
		goalID: resolvedGoalId,
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchGoalActivity } = useGoalHistory({
		goalID: resolvedGoalId,
		sortDirection: "desc",
		sortName: "_id",
	});

	const { userCanEditValue, userCanEditStatus } = parseGoalPermissionsForUser(permissions);

	const {
		queryParams: { modal, initialGoalProgressValue },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : modal === "updateProgress";

	const handleClose = (): void => {
		if (onClose) {
			onClose();
			return;
		}
		handleQueryChange({ modal: undefined, initialGoalProgressValue: undefined });
	};

	// Mutation
	const { mutate, isPending } = useMutateGoalDetails(resolvedGoalId, {
		onSuccess: () => {
			refetchGoal();
			refetchGoalActivity();
			refetchGoalProgress();
			onSuccess?.();
			handleClose();
		},
		onError: () => {
			toast.error("Something went wrong, please try again");
		},
	});

	// Default form values
	const defaultValues = useMemo(
		(): FormValues => ({
			goalValue: typeof initialGoalProgressValue === "number" ? initialGoalProgressValue : (goal?.value ?? null),
			incrementValue: null,
			isIncrement: false,
			message: "",
			asOfDate: new Date(),
			outlookId: goal?.color?._id ?? null,
		}),
		[goal?.value, goal?.color?._id, initialGoalProgressValue]
	);

	// Track if the form has been initialized for the current modal session
	// This prevents resetting the form multiple times and ensures we wait for goal data
	const [hasInitialized, setHasInitialized] = useState(false);

	// Reset the initialization flag when modal closes
	useEffect(() => {
		if (!isOpen) {
			setHasInitialized(false);
		}
	}, [isOpen]);

	// Reset form when modal is open and goal data is available
	// Only reset once per modal session to avoid overwriting user changes on refetch
	useEffect(() => {
		if (isOpen && formRef.current && goal?._id && !hasInitialized) {
			formRef.current.reset();
			setHasInitialized(true);
		}
	}, [isOpen, goal?._id, hasInitialized]);

	// Handle form submit
	const handleSubmit = async ({ value }: { value: FormValues }): Promise<void> => {
		if (!goal?._id) {
			return;
		}

		const currentTime = new Date();
		const newValue =
			value.isIncrement && value.incrementValue !== null
				? (goal?.value ?? 0) + value.incrementValue
				: value.goalValue;
		const newColor = value.outlookId && value.outlookId !== "none" ? value.outlookId : null;
		const newDate = value.asOfDate
			? set(value.asOfDate, {
					hours: currentTime.getHours(),
					minutes: currentTime.getMinutes(),
					seconds: currentTime.getSeconds(),
				}).toISOString()
			: null;

		const updates: Array<{ field: string; value: unknown }> = [];

		if (newValue !== null && newValue !== undefined) {
			updates.push({ field: "value", value: newValue });
		}

		if (value.message) {
			updates.push({ field: "message", value: value.message });
		}

		if (newDate) {
			updates.push({ field: "as_of", value: newDate });
		}

		if (newColor) {
			updates.push({ field: "color", value: newColor });
		} else if (goal?.color) {
			updates.push({ field: "color", value: null });
		}

		mutate(updates);
	};

	// Labels for display
	const targetLabels = getProgressLabels({ value: goal?.target, units: goal?.units });

	// Outlook options for SingleSelect
	const outlookOptions = useMemo(() => {
		if (!outlooksData?.colors) return [];
		return outlooksData.colors.map((color) => ({
			label: color.name,
			value: color.id,
			color: color.color,
		}));
	}, [outlooksData?.colors]);

	// Custom render for outlook options (with colored dot)
	const renderOutlookOption = (option: {
		label: React.ReactNode;
		value: string;
		color?: string;
	}): React.ReactNode => (
		<div className="flex items-center gap-2">
			{option.color && <div className="size-3 shrink-0 rounded-full" style={{ backgroundColor: option.color }} />}
			<Text size="paragraph-3" weight="regular">
				{option.label}
			</Text>
		</div>
	);

	// Custom render for selected outlook value
	const renderOutlookValue = (value: string): React.ReactNode => {
		const option = outlookOptions.find((opt) => opt.value === value);
		if (!option) return null;
		return (
			<div className="flex items-center gap-2">
				{option.color && (
					<div className="size-3 shrink-0 rounded-full" style={{ backgroundColor: option.color }} />
				)}
				<Text size="paragraph-3" weight="regular">
					{option.label}
				</Text>
			</div>
		);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={handleClose} dangerouslySetClassName="w-[432px]">
			{/* Header: centered title + subtitle */}
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Update Progress
				</Text>
				<Text size="paragraph-2" weight="regular">
					Target: {targetLabels?.mainText || ""} {targetLabels?.subText || ""}
				</Text>
			</div>

			{/* Form */}
			<PYForm
				ref={formRef}
				id="update-progress"
				schema={formSchema}
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
			>
				<div className="flex flex-col gap-2 px-6 pb-6">
					{/* Goal Value Input - toggles between increment and replace mode */}
					<GoalValueSection goalValue={goal?.value ?? 0} units={goal?.units ?? ""} />

					{/* Message */}
					<FormTextEditor
						id="message"
						label="Message"
						placeholder="Add Message"
						minHeight={68}
						maxHeight={100}
						data-testid="update-progress-message"
					/>

					{/* As of Date */}
					<FormDatePicker id="asOfDate" label="As of Date" data-testid="update-progress-as-of-date" />

					{/* Outlook picker - only show if user can edit status */}
					{userCanEditStatus && (
						<FormSingleSelect<string>
							id="outlookId"
							label="Outlook"
							placeholder="Make a selection"
							options={outlookOptions}
							isClearable
							renderOption={renderOutlookOption}
							renderValue={renderOutlookValue}
							disabled={isLoadingOutlooks}
							data-testid="update-progress-outlook"
						/>
					)}
				</div>

				{/* Footer: centered buttons */}
				<div className="flex justify-center gap-2 px-6 pb-6">
					<div className="w-fit">
						<ButtonStandard
							variant="outline"
							color="neutral"
							size="large"
							onClick={handleClose}
							data-testid="cancel-update-progress"
						>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-fit min-w-[90px]">
						<SubmitButton
							color="primary"
							size="large"
							disabled={!userCanEditValue}
							loading={isPending}
							data-testid="update-progress"
						>
							Update
						</SubmitButton>
					</div>
				</div>
			</PYForm>
		</ModalBase>
	);
};

// Component for the goal value section with increment toggle
type GoalValueSectionProps = {
	goalValue: number;
	units: Goal["units"];
};

const GoalValueSection = ({ goalValue, units }: GoalValueSectionProps): React.ReactElement => {
	const form = usePyForm<typeof formSchema>();

	const labels = getProgressLabels({
		value: goalValue,
		units,
		truncateSubtextChars: 10,
	});

	return (
		<FormSubscribe<typeof formSchema, { isIncrement: boolean; incrementValue: number | null }>
			id="goal-value-section"
			selector={(state) => ({
				isIncrement: state.values.isIncrement,
				incrementValue: state.values.incrementValue,
			})}
		>
			{({ isIncrement, incrementValue }) => {
				const incrementPostLabels = getProgressLabels({
					value: Number(incrementValue || 0) + goalValue,
					units,
					truncateSubtextChars: 10,
					decimalPlacesOverride: 2,
				});

				return (
					<>
						{isIncrement ? (
							<div>
								<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="mb-2">
									Update Goal Value
								</Text>
								<div className="flex items-end gap-1">
									<div className="w-32 shrink-0">
										<FormInputNumeric
											id="incrementValue"
											placeholder="Add value"
											allowEmpty
											data-testid="update-progress-increment-value"
										/>
									</div>
									<div className="flex h-8 items-center">
										<Text size="paragraph-3" weight="regular">
											+ {labels.mainText} = {incrementPostLabels.mainText}
										</Text>
									</div>
								</div>
							</div>
						) : (
							<FormInputNumeric
								id="goalValue"
								label="Update Goal Value"
								placeholder="0"
								allowEmpty
								data-testid="update-progress-goal-value"
							/>
						)}

						{/* Increment checkbox */}
						<div className="mt-2 flex items-center gap-2">
							<Checkbox
								id="increment-checkbox"
								checked={isIncrement}
								onCheckedChange={(checked) => {
									form.setFieldValue("isIncrement", checked);
								}}
								aria-label="Increment Value"
							/>
							<div className="flex items-center gap-1">
								<Text size="paragraph-3" weight="semi-bold">
									Increment Value
								</Text>
								<Tooltip
									content="Increment value will add this value to the current value of this goal. If you do not check this box, this value will replace the current value."
									placement="right"
								>
									<Icon icon={faCircleInfo} size={12} color="neutral-5" />
								</Tooltip>
							</div>
						</div>
					</>
				);
			}}
		</FormSubscribe>
	);
};

export default UpdateProgressModal;
